import React, { useState } from "react";
import { KeyRound, Plus, Trash2, UserPlus } from "lucide-react";
import { api, fmtDate, invalidate, timeAgo, toast, toastError, useAuth, useQuery, useStatus } from "../lib";
import { Badge, Button, CopyButton, Dot, Field, Modal } from "../ui";

const PROVIDERS: Record<string, string> = {
  anthropic: "Anthropic (API)",
  openai: "OpenAI",
  gemini: "Google Gemini",
  claude: "Claude (assinatura, chamada simples)",
  "claude-code": "Claude Code (agentes)",
  codex: "Codex CLI (agentes, experimental)",
  chatgpt: "ChatGPT (assinatura, codex exec)",
  fake: "Provedor de teste",
};

export function SettingsPage() {
  const status = useStatus();
  const auth = useAuth();
  const { data: keys } = useQuery<any>("/api/keys");
  const { data: projects } = useQuery<any[]>("/api/projects");
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [scope, setScope] = useState<"app" | "admin">("app");
  const [project, setProject] = useState("");
  const [created, setCreated] = useState<string | null>(null);
  const create = async () => {
    try {
      const k = await api.post("/api/keys", { name, scope, project: scope === "app" ? project || null : null });
      setCreated(k.key);
      setName("");
      invalidate("/api/keys");
    } catch (e) {
      toastError(e);
    }
  };
  const mask = (k: string) => k.slice(0, 10) + "•".repeat(12) + k.slice(-4);
  const agents = status?.agents;
  return (
    <>
      <div className="topbar"><div className="crumbs"><span className="cur">Configurações</span></div></div>
      <div className="content">
        <div className="page page-narrow">
          <div className="page-head"><div><h1 className="page-title">Configurações</h1><div className="page-sub">Dados em <span className="mono">{status?.home}</span>. Cada versão é uma pasta imutável; execuções, cache e avaliações ficam num SQLite ao lado.</div></div></div>

          {auth.status === "in" && <PeopleCard me={auth.user!.email} dock={status?.loginVia === "dock"} />}

          <div className={auth.status === "in" ? "section card" : "card"}>
            <div className="card-head"><span className="card-title">Provedores</span></div>
            <table className="table">
              <tbody>
                {Object.entries(status?.providers ?? {}).map(([k, v]) => (
                  <tr key={k}>
                    <td style={{ width: 20 }}><Dot tone={!v.ok ? "red" : v.lastError ? "amber" : "green"} /></td>
                    <td><b>{PROVIDERS[k] ?? k}</b><div className="faint" style={{ fontSize: 12 }}>{v.detail}</div></td>
                    <td style={{ maxWidth: 380 }}>
                      {!v.ok ? <Badge tone="red">indisponível</Badge> : v.lastError ? (
                        <div><Badge tone="amber">última chamada falhou</Badge><div className="faint" style={{ fontSize: 12, marginTop: 4 }}>{v.lastError.slice(0, 180)}</div></div>
                      ) : <Badge tone="green">ok</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="card-foot faint" style={{ fontSize: 12 }}>
              Agentes: {agents?.enabled ? "rodam no Seatbelt do macOS (escrita só no workspace)." : agents?.reason ?? "…"} Juiz padrão: <span className="mono">{status?.judgeModel}</span>.
            </div>
          </div>

          <div className="section card">
            <div className="card-head"><span className="card-title">Chaves de API</span><Button size="sm" icon={<Plus size={13} />} onClick={() => (setCreating(true), setCreated(null))}>Nova chave</Button></div>
            {keys?.local && (
              <div className="card-body row" style={{ borderBottom: "1px solid var(--border)" }}>
                <KeyRound size={15} className="faint" />
                <div className="grow"><b>local</b><div className="mono faint" style={{ fontSize: 12 }}>{mask(keys.local)}</div></div>
                <span className="faint" style={{ fontSize: 12 }}>usada nos exemplos da interface</span>
                <CopyButton text={keys.local} label="Copiar" />
              </div>
            )}
            <table className="table">
              <thead><tr><th>Nome</th><th>Acesso</th><th>Prefixo</th><th>Criada</th><th>Último uso</th><th /></tr></thead>
              <tbody>
                {(keys?.keys ?? []).map((k: any) => (
                  <tr key={k.id} style={k.revoked ? { opacity: 0.45 } : undefined}>
                    <td>{k.name} {k.revoked ? <Badge tone="red">revogada</Badge> : null}</td>
                    <td>{k.scope === "admin" ? <Badge tone="amber">admin</Badge> : <span className="row" style={{ gap: 6 }}><Badge tone="blue">app</Badge><span className="faint mono" style={{ fontSize: 12 }}>{k.project ?? "todos os projetos"}</span></span>}</td>
                    <td className="mono faint">{k.prefix}…</td>
                    <td className="faint">{fmtDate(k.created_at)}</td>
                    <td className="faint">{k.last_used_at ? timeAgo(k.last_used_at) : "nunca"}</td>
                    <td className="r">{!k.revoked && k.name !== "local" && <Button size="xs" variant="ghost" icon={<Trash2 size={12} />} onClick={async () => { await api.del("/api/keys/" + k.id).catch(toastError); invalidate("/api/keys"); }}>Revogar</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="card-foot faint" style={{ fontSize: 12 }}>Chave de app: só chama os alvos publicados (/v1), e só do projeto dela. Chave admin: CLI e tudo o que a interface faz.</div>
          </div>

          <div className="section card">
            <div className="card-head"><span className="card-title">Modelos no catálogo</span><span className="faint" style={{ fontSize: 12 }}>qualquer outro vale como provedor/modelo</span></div>
            <table className="table">
              <thead><tr><th>Modelo</th><th>Tipo</th><th>Cobrança</th><th className="r">US$ / 1M tokens</th></tr></thead>
              <tbody>
                {(status?.models ?? []).map((m) => (
                  <tr key={m.id}>
                    <td><div>{m.label}</div><div className="mono faint" style={{ fontSize: 11.5 }}>{m.id}</div></td>
                    <td><Badge tone={m.kind === "agent" ? "blue" : m.kind === "image" ? "amber" : "violet"}>{m.kind === "agent" ? "agente" : m.kind === "image" ? "imagem" : "texto"}</Badge></td>
                    <td className="faint">{m.basis === "subscription" ? "assinatura" : m.basis === "free" ? "grátis" : "API"}</td>
                    <td className="r mono faint" style={{ fontSize: 12 }}>{m.price ? m.price.in + " / " + m.price.out : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Modal open={creating} onClose={() => setCreating(false)} title="Nova chave de API" footer={created ? <Button variant="primary" onClick={() => setCreating(false)}>Pronto</Button> : <><Button variant="ghost" onClick={() => setCreating(false)}>Cancelar</Button><Button variant="primary" onClick={create}>Criar</Button></>}>
        {created ? (
          <>
            <div className="muted">Copie agora: a chave não aparece de novo.</div>
            <div className="endpoint"><span className="url">{created}</span><CopyButton text={created} /></div>
          </>
        ) : (
          <>
            <Field label="Nome"><input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: produção do Pejota" /></Field>
            <Field label="Acesso">
              <select className="select" value={scope} onChange={(e) => setScope(e.target.value as any)}>
                <option value="app">App: só chama os alvos publicados (/v1)</option>
                <option value="admin">Admin: CLI e tudo o que a interface faz</option>
              </select>
            </Field>
            {scope === "app" && (
              <Field label="Projeto" hint="A chave só chama os alvos deste projeto.">
                <select className="select" value={project} onChange={(e) => setProject(e.target.value)}>
                  <option value="">Todos os projetos</option>
                  {(projects ?? []).map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                </select>
              </Field>
            )}
          </>
        )}
      </Modal>
    </>
  );
}

function PeopleCard({ me, dock }: { me: string; dock: boolean }) {
  const { data: users } = useQuery<any[]>("/api/users");
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const invite = async (target = email) => {
    setBusy(true);
    try {
      const r = await api.post("/api/users/invite", { email: target });
      setLink(r.url);
      setOpen(true);
      invalidate("/api/users");
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  };
  const remove = async (u: any) => {
    try {
      await api.del("/api/users/" + u.id);
      toast(dock ? "Sessões encerradas" : "Acesso removido", u.email);
      invalidate("/api/users");
    } catch (e) {
      toastError(e);
    }
  };
  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">Pessoas com acesso</span>
        {dock ? <span className="faint" style={{ fontSize: 12 }}>pelo Dock</span> : <Button size="sm" icon={<UserPlus size={13} />} onClick={() => (setOpen(true), setLink(null), setEmail(""))}>Convidar</Button>}
      </div>
      {dock && <div className="card-note">Quem é da PQG no Dock entra com o mesmo e-mail e senha. A lista mostra quem já entrou; quem sai da PQG no Dock não entra mais.</div>}
      <table className="table">
        <thead><tr><th>Pessoa</th><th>Último acesso</th><th /></tr></thead>
        <tbody>
          {(users ?? []).map((u) => (
            <tr key={u.id}>
              <td><b>{u.name}</b>{u.email === me && <span className="faint"> (você)</span>}<div className="faint" style={{ fontSize: 12 }}>{u.email}</div></td>
              <td className="faint">{u.lastLoginAt ? timeAgo(u.lastLoginAt) : "nunca entrou"}</td>
              <td className="r">
                <span className="row" style={{ justifyContent: "flex-end", gap: 4 }}>
                  {!dock && <Button size="xs" variant="ghost" onClick={() => (setEmail(u.email), invite(u.email))}>Link de nova senha</Button>}
                  {u.email !== me && <Button size="xs" variant="ghost" icon={<Trash2 size={12} />} onClick={() => remove(u)}>{dock ? "Encerrar sessões" : "Remover"}</Button>}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Modal open={open} onClose={() => setOpen(false)} title={link ? "Link de convite" : "Convidar pessoa"} footer={link ? <Button variant="primary" onClick={() => setOpen(false)}>Pronto</Button> : <><Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button><Button variant="primary" loading={busy} disabled={!email.includes("@")} onClick={() => invite()}>Gerar link</Button></>}>
        {link ? (
          <>
            <div className="muted">Mande este link para <b>{email}</b>. Ele vale uma vez só, por 72 horas, e é por ele que a pessoa escolhe a senha.</div>
            <div className="endpoint"><span className="url">{link}</span><CopyButton text={link} /></div>
          </>
        ) : (
          <Field label="E-mail"><input className="input" type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="pessoa@empresa.com" /></Field>
        )}
      </Modal>
    </div>
  );
}
